import { useMemo, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useContent } from '@/context/ContentContext';
import { X } from 'lucide-react';
import type { SeasonalEffectType } from '@/types/content';

interface Particle {
  id: number;
  x: number;
  delay: number;
  duration: number;
  size: number;
  rotation?: number;
}

const generateParticles = (count: number): Particle[] => 
  Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 5,
    duration: Math.random() * 5 + 5,
    size: Math.random() * 15 + 10,
    rotation: Math.random() * 360,
  }));

// Snow Effect
const SnowEffect = ({ intensity }: { intensity: number }) => {
  const particles = useMemo(() => generateParticles(intensity), [intensity]);

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute text-white"
          style={{ left: `${p.x}%`, top: -20, fontSize: p.size }}
          animate={{
            y: ['0vh', '105vh'],
            x: [0, Math.sin(p.id) * 30, 0],
            rotate: [0, 360],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            delay: p.delay,
            ease: 'linear',
          }}
        >
          ❄
        </motion.div>
      ))}
    </div>
  );
};

// Autumn Leaves Effect
const AutumnLeavesEffect = ({ intensity }: { intensity: number }) => {
  const particles = useMemo(() => generateParticles(intensity), [intensity]);
  const leaves = ['🍂', '🍁', '🍃', '🌾'];

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute"
          style={{ left: `${p.x}%`, top: -30, fontSize: p.size }}
          animate={{
            y: ['0vh', '105vh'],
            x: [0, Math.sin(p.id) * 50, -30, Math.cos(p.id) * 40, 0],
            rotate: [0, 360, 180, 540],
          }}
          transition={{
            duration: p.duration + 3,
            repeat: Infinity,
            delay: p.delay,
            ease: 'easeInOut',
          }}
        >
          {leaves[p.id % leaves.length]}
        </motion.div>
      ))}
    </div>
  );
};

// Cherry Blossoms Effect
const CherryBlossomsEffect = ({ intensity }: { intensity: number }) => {
  const particles = useMemo(() => generateParticles(intensity), [intensity]);

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute"
          style={{ left: `${p.x}%`, top: -20, fontSize: p.size }}
          animate={{
            y: ['0vh', '105vh'],
            x: [0, 20, -20, 10, 0],
            rotate: [0, 180, 360],
            opacity: [1, 0.8, 1],
          }}
          transition={{
            duration: p.duration + 2,
            repeat: Infinity,
            delay: p.delay,
            ease: 'easeInOut',
          }}
        >
          🌸
        </motion.div>
      ))}
    </div>
  );
};

// Fireworks Effect
const FireworksEffect = ({ intensity }: { intensity: number }) => {
  const [fireworks, setFireworks] = useState<{ id: number; x: number; y: number; color: string }[]>([]);
  const colors = ['#ff0080', '#00ffff', '#ffff00', '#ff00ff', '#00ff00', '#ff6600'];

  useEffect(() => {
    const interval = setInterval(() => {
      const newFirework = {
        id: Date.now(),
        x: Math.random() * 80 + 10,
        y: Math.random() * 40 + 20,
        color: colors[Math.floor(Math.random() * colors.length)],
      };
      setFireworks((prev) => [...prev.slice(-intensity), newFirework]);
    }, 2000 / (intensity / 10));

    return () => clearInterval(interval);
  }, [intensity]);

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      <AnimatePresence>
        {fireworks.map((fw) => (
          <motion.div
            key={fw.id}
            className="absolute"
            style={{ left: `${fw.x}%`, top: `${fw.y}%` }}
            initial={{ scale: 0, opacity: 1 }}
            animate={{ scale: [0, 1.5, 2], opacity: [1, 1, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 rounded-full"
                style={{ backgroundColor: fw.color }}
                animate={{
                  x: Math.cos((i * 30 * Math.PI) / 180) * 80,
                  y: Math.sin((i * 30 * Math.PI) / 180) * 80,
                  opacity: [1, 0],
                  scale: [1, 0.5],
                }}
                transition={{ duration: 1, ease: 'easeOut' }}
              />
            ))}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

// Hearts Effect
const HeartsEffect = ({ intensity }: { intensity: number }) => {
  const particles = useMemo(() => generateParticles(intensity), [intensity]);

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute text-pink-500"
          style={{ left: `${p.x}%`, bottom: -30, fontSize: p.size }}
          animate={{
            y: [0, -window.innerHeight - 50],
            x: [0, Math.sin(p.id) * 30, 0],
            scale: [0.5, 1, 0.5],
            opacity: [0, 1, 0],
          }}
          transition={{
            duration: p.duration + 2,
            repeat: Infinity,
            delay: p.delay,
            ease: 'easeOut',
          }}
        >
          ❤️
        </motion.div>
      ))}
    </div>
  );
};

// Confetti Effect
const ConfettiEffect = ({ intensity }: { intensity: number }) => {
  const particles = useMemo(() => generateParticles(intensity * 2), [intensity]);
  const colors = ['#ff0080', '#00ffff', '#ffff00', '#ff00ff', '#00ff00', '#ff6600', '#0066ff'];

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute w-3 h-3"
          style={{
            left: `${p.x}%`,
            top: -20,
            backgroundColor: colors[p.id % colors.length],
            borderRadius: p.id % 2 === 0 ? '50%' : '0',
          }}
          animate={{
            y: ['0vh', '105vh'],
            x: [0, Math.sin(p.id) * 100, -50, Math.cos(p.id) * 80],
            rotate: [0, 720],
          }}
          transition={{
            duration: p.duration + 2,
            repeat: Infinity,
            delay: p.delay,
            ease: 'linear',
          }}
        />
      ))}
    </div>
  );
};

// Halloween Effect
const HalloweenEffect = ({ intensity }: { intensity: number }) => {
  const particles = useMemo(() => generateParticles(intensity), [intensity]);
  const items = ['🦇', '🎃', '👻', '🕷️', '💀'];

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute"
          style={{ left: `${p.x}%`, top: `${Math.random() * 80}%`, fontSize: p.size + 5 }}
          animate={{
            x: [0, 30, -30, 0],
            y: [0, -20, 20, 0],
            rotate: p.id % 2 === 0 ? [0, 10, -10, 0] : undefined,
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            delay: p.delay,
            ease: 'easeInOut',
          }}
        >
          {items[p.id % items.length]}
        </motion.div>
      ))}
    </div>
  );
};

// Easter Eggs Effect
const EasterEggsEffect = ({ intensity }: { intensity: number }) => {
  const particles = useMemo(() => generateParticles(intensity), [intensity]);
  const eggs = ['🥚', '🐰', '🐣', '🌷', '🦋'];

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute"
          style={{ left: `${p.x}%`, bottom: -30, fontSize: p.size }}
          animate={{
            y: [0, -window.innerHeight * 0.3, 0],
            x: [0, Math.sin(p.id) * 20, 0],
            rotate: [0, 20, -20, 0],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            delay: p.delay,
            ease: 'easeInOut',
          }}
        >
          {eggs[p.id % eggs.length]}
        </motion.div>
      ))}
    </div>
  );
};

// Stars Sparkle Effect
const StarsSparkleEffect = ({ intensity }: { intensity: number }) => {
  const particles = useMemo(() => generateParticles(intensity), [intensity]);

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute"
          style={{ left: `${p.x}%`, top: `${Math.random() * 100}%`, fontSize: p.size }}
          animate={{
            scale: [0, 1, 0],
            opacity: [0, 1, 0],
            rotate: [0, 180],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            delay: p.delay,
            ease: 'easeInOut',
          }}
        >
          ✨
        </motion.div>
      ))}
    </div>
  );
};

const SeasonalEffects = () => {
  const { content } = useContent();
  const settings = content.site_settings?.seasonal;
  const [dismissed, setDismissed] = useState(false);

  // Check if effect should be active based on dates
  const isActive = useMemo(() => {
    if (!settings?.enabled) return false;
    if (!settings.start_date && !settings.end_date) return true;

    const now = new Date();
    const start = settings.start_date ? new Date(settings.start_date) : null;
    const end = settings.end_date ? new Date(settings.end_date) : null;

    if (start && now < start) return false;
    if (end && now > end) return false;

    return true;
  }, [settings]);

  if (!isActive || settings?.effect === 'none') return null;

  const intensityMap = { low: 15, medium: 30, high: 50 };
  const intensity = intensityMap[settings?.intensity || 'medium'];

  const renderEffect = (effect: SeasonalEffectType) => {
    switch (effect) {
      case 'snow': return <SnowEffect intensity={intensity} />;
      case 'autumn_leaves': return <AutumnLeavesEffect intensity={intensity} />;
      case 'cherry_blossoms': return <CherryBlossomsEffect intensity={intensity} />;
      case 'fireworks': return <FireworksEffect intensity={intensity} />;
      case 'hearts': return <HeartsEffect intensity={intensity} />;
      case 'confetti': return <ConfettiEffect intensity={intensity} />;
      case 'halloween': return <HalloweenEffect intensity={intensity} />;
      case 'easter_eggs': return <EasterEggsEffect intensity={intensity} />;
      case 'stars_sparkle': return <StarsSparkleEffect intensity={intensity} />;
      default: return null;
    }
  };

  return (
    <>
      {renderEffect(settings?.effect || 'none')}
      
      {/* Seasonal Message Banner */}
      <AnimatePresence>
        {settings?.show_message && settings?.custom_message && !dismissed && (
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-brand-purple via-brand-pink to-brand-cyan text-white py-3 px-4 text-center shadow-lg"
          >
            <div className="container mx-auto flex items-center justify-center gap-4">
              <span className="font-medium">{settings.custom_message}</span>
              <button
                onClick={() => setDismissed(true)}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SeasonalEffects;